import { useState, useEffect, useMemo, useCallback } from 'react'
import { useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query'
import { subDays } from 'date-fns'
import { queryKeys } from '@/api/queryKeys'
import { fetchCoachingContext } from '@/api/coachingContext'
import { getCachedSummary, saveSummary } from '@/api/aiSummaries'
import { estimateCoaching, streamCoaching } from '@/api/coaching'
import { readLetterStream } from '@/lib/anthropicStream'
import { CUT_OFF_MESSAGE, DECLINED_MESSAGE, parseCoachingBody } from '@/lib/coachingBody'
import { letterSnapshotId, sha256hex } from '@/lib/letterSnapshot'
import { computeAllocation } from '@/lib/allocation'
import type { JournalEntry, AiSummary, FeedbackRating } from '@/types'
import type {
  CoachingMode,
  CoachingContext,
  CoachingEstimate,
  CoachingRequest,
  CoachingResponse,
  PriorityCtx,
  BucketCtx,
  WeeklyReviewCtx,
} from '@/types/coaching'

const DEFAULT_AMBER = 55
const DEFAULT_RED = 65
const JOURNAL_WINDOW_DAYS = 30
const JOURNAL_THEME_LIMIT = 10


function stripMarkdown(text: string): string {
  return text
    .replace(/^#{1,6}\s+/gm, '')
    .replace(/^\s*[-*+]\s+/gm, '')
    .replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
    .replace(/[*_`~]/g, '')
    .replace(/\n{2,}/g, ' ')
    .trim()
}

function extractThemes(entries: JournalEntry[]): string[] {
  return entries
    .filter(e => !e.aiExcludable)
    .map(e => {
      const clean = stripMarkdown(e.body)
      const sentence = clean.split(/[.!?\n]/).find(s => s.trim().length > 10)
      return sentence?.trim().slice(0, 120) ?? ''
    })
    .filter(Boolean)
}

function buildFeedbackContext(s: AiSummary): string {
  const label =
    s.feedbackRating === 'landed' ? 'very helpful'
    : s.feedbackRating === 'mixed' ? 'partially helpful'
    : 'missed the mark'
  const base = `The previous coaching letter was rated "${label}".`
  return s.feedbackNote ? `${base} Reader's note: "${s.feedbackNote}"` : base
}

function buildFullJournalText(entries: JournalEntry[]): string {
  return entries
    .filter(e => !e.aiExcludable)
    .map(e => `[${e.entryDate}]\n${e.body.trim()}`)
    .join('\n\n---\n\n')
}

export type { FeedbackRating }

export interface UseCoachingReturn {
  context: CoachingContext | undefined
  snapshotId: string | undefined
  isLoading: boolean
  summaries: AiSummary[]
  journalExcludedCount: number
  buildRequest: (mode: CoachingMode, includeFullJournal: boolean, model?: string) => Promise<CoachingRequest>
  generate: (mode: CoachingMode, includeFullJournal?: boolean, model?: string, estimatedCostUsd?: number) => Promise<CoachingResponse>
  isGenerating: boolean
  /** The letter while it streams in: summarized thinking first, then the letter's JSON text. */
  live: LiveLetter | undefined
  result: CoachingResponse | undefined
  error: string | undefined
  clearResult: () => void
}

export interface LiveLetter {
  thinking: string
  text: string
}

export function useCoaching(): UseCoachingReturn {
  const qc = useQueryClient()
  const [isGenerating, setIsGenerating] = useState(false)
  const [result, setResult] = useState<CoachingResponse | undefined>()
  const [live, setLive] = useState<LiveLetter | undefined>()
  const [error, setError] = useState<string | undefined>()
  const [snapshotId, setSnapshotId] = useState<string | undefined>()

  const { data: composite } = useQuery({
    queryKey: queryKeys.coachingContext(),
    queryFn: fetchCoachingContext,
  })

  const data = useMemo(() => {
    if (!composite) return undefined
    return {
      priorities: composite.priorities,
      buckets: composite.buckets,
      settings: composite.settings,
      recentJournals: composite.recentJournals,
      weeklyReviews: composite.weeklyReviews,
    }
  }, [composite])

  const summaries = composite?.summaries ?? []

  const context = useMemo((): CoachingContext | undefined => {
    if (!data) return undefined
    const { priorities, buckets, settings, recentJournals, weeklyReviews } = data

    const to = new Date()
    const from = subDays(to, JOURNAL_WINDOW_DAYS - 1)
    const alloc = computeAllocation(
      priorities,
      buckets,
      from,
      to,
      settings?.bandwidthAmberHours ?? DEFAULT_AMBER,
      settings?.bandwidthRedHours ?? DEFAULT_RED,
    )

    const bucketMap = new Map(buckets.map(b => [b.id, b]))

    const priorityCtxs: PriorityCtx[] = priorities
      .filter(p => !p.archivedAt && !p.aiExcludable && p.status !== 'released' && p.status !== 'completed')
      .map(p => {
        const bucket = bucketMap.get(p.bucketId ?? '')
        return {
          id: p.id,
          title: p.title,
          bucketName: bucket?.name ?? 'Unsorted',
          bucketRank: bucket?.rank ?? 999,
          priorityRank: p.rank,
          status: p.status,
          cadence: p.cadence,
          hoursPerWeek: p.hoursPerWeek,
          nextReviewDate: p.nextReviewDate,
          description: p.description,
          origin: p.origin,
          energy: p.energy,
          callingConfidence: p.callingConfidence,
          actualShare: alloc.byPriority[p.id],
        }
      })
      .sort((a, b) => a.bucketRank - b.bucketRank || a.priorityRank - b.priorityRank)

    const bucketCtxs: BucketCtx[] = alloc.byBucket
      .map(ba => {
        const b = bucketMap.get(ba.bucketId)
        return {
          id: ba.bucketId,
          name: b?.name ?? ba.bucketId,
          rank: b?.rank ?? 999,
          share: ba.share,
          targetAlloc: ba.targetAlloc,
          gap: ba.gap,
          intention: b?.intention,
        }
      })
      .sort((a, b) => a.rank - b.rank)

    const journalThemes = extractThemes(recentJournals.slice(0, JOURNAL_THEME_LIMIT))

    // FR-AI15: include feedback from the most recent rated letter
    const recentFeedback = summaries.find(s => s.feedbackRating !== undefined)
    const feedbackContext = recentFeedback ? buildFeedbackContext(recentFeedback) : undefined

    // CL-7 step 6: up to 3 most recent weekly reviews (structure only)
    const recentWeeklyReviews: WeeklyReviewCtx[] = weeklyReviews.map(r => ({
      periodStart: r.periodStart,
      completedAt: r.completedAt,
    }))

    return {
      priorities: priorityCtxs,
      byBucket: bucketCtxs,
      alignmentScore: alloc.alignmentScore,
      committedHoursPerWeek: alloc.committedHoursPerWeek,
      bandwidthStatus: alloc.bandwidthStatus,
      journalThemes,
      feedbackContext,
      recentWeeklyReviews: recentWeeklyReviews.length > 0 ? recentWeeklyReviews : undefined,
    }
  }, [data, summaries])

  const journalExcludedCount = useMemo(
    () => data?.recentJournals.filter(e => e.aiExcludable).length ?? 0,
    [data],
  )

  // SHA-256 of the themes-only context — changes whenever priorities/buckets/journals change
  useEffect(() => {
    if (!context) { setSnapshotId(undefined); return }
    let cancelled = false
    sha256hex(JSON.stringify(context)).then(hash => {
      if (!cancelled) setSnapshotId(hash)
    })
    return () => { cancelled = true }
  }, [context])

  const buildRequest = useCallback(async (
    mode: CoachingMode,
    includeFullJournal: boolean,
    model?: string,
  ): Promise<CoachingRequest> => {
    if (!context || !data) throw new Error('Coaching context is not ready yet')
    const payload: CoachingContext = includeFullJournal
      ? { ...context, fullJournalText: buildFullJournalText(data.recentJournals) }
      : context
    return {
      // Hash mode + payload so different modes or journal opts get distinct ids (FR-AI14)
      snapshotId: await letterSnapshotId(mode, model, payload),
      coachingMode: mode,
      includeFullJournal,
      model,
      context: payload,
    }
  }, [context, data])

  const generate = useCallback(async (
    mode: CoachingMode,
    includeFullJournal = false,
    model?: string,
    /** The typical cost shown before Generate, saved to compare with the actual cost. */
    estimatedCostUsd?: number,
  ): Promise<CoachingResponse> => {
    setIsGenerating(true)
    setError(undefined)

    try {
      const req = await buildRequest(mode, includeFullJournal, model)
      const payloadHash = req.snapshotId

      // FR-AI14: return cached letter if context hasn't changed
      const cached = await getCachedSummary(payloadHash)
      if (cached) {
        const cachedResponse: CoachingResponse = {
          snapshotId: cached.snapshotId,
          cached: true,
          distressFloorTriggered: cached.distressFloorTriggered,
          thinking: cached.thinking,
          model: cached.model,
          body: cached.body,
        }
        setResult(cachedResponse)
        return cachedResponse
      }

      const res = await streamCoaching(req)

      // FR-AI10: when the distress floor trips, the server answers with JSON and no letter.
      if (res.headers.get('Content-Type')?.includes('application/json')) {
        const distress = (await res.json()) as CoachingResponse
        setResult(distress)
        return distress
      }

      let thinking = ''
      let text = ''
      setLive({ thinking, text })
      const outcome = await readLetterStream(res.body!, {
        onThinking: (delta) => { thinking += delta; setLive({ thinking, text }) },
        onText: (delta) => { text += delta; setLive({ thinking, text }) },
        // The requested model declined, and the fallback model writes the letter from the start.
        onFallback: () => { thinking = ''; text = ''; setLive({ thinking, text }) },
      })
      if (outcome.stopReason === 'max_tokens') throw new Error(CUT_OFF_MESSAGE)
      if (outcome.stopReason === 'refusal') throw new Error(DECLINED_MESSAGE)

      // Record the model as chosen (the API's id may carry a date suffix), or the fallback that wrote it.
      const letterModel = outcome.fallbackModel ?? model ?? outcome.model
      const response: CoachingResponse = {
        snapshotId: payloadHash,
        cached: false,
        distressFloorTriggered: false,
        thinking: thinking || null,
        model: letterModel,
        inputTokens: outcome.inputTokens,
        outputTokens: outcome.outputTokens,
        body: parseCoachingBody(text, req.context.alignmentScore, req.context.priorities.map((p) => p.id)),
      }

      // Persist for idempotency (FR-AI14), with the thinking kept for History.
      await saveSummary(payloadHash, mode, includeFullJournal, response.body, {
        thinking: thinking || undefined,
        model: letterModel ?? undefined,
        inputTokens: outcome.inputTokens ?? undefined,
        outputTokens: outcome.outputTokens ?? undefined,
        estimatedCostUsd,
      })
      void qc.invalidateQueries({ queryKey: queryKeys.aiSummaries.all() })
      void qc.invalidateQueries({ queryKey: queryKeys.aiSummaries.latestInsights() })
      void qc.invalidateQueries({ queryKey: queryKeys.coachingContext() })
      void qc.invalidateQueries({ queryKey: ['coachingEstimate'] })

      setResult(response)
      return response
    } catch (e) {
      const msg = e instanceof Error ? e.message : String(e)
      setError(msg)
      throw e
    } finally {
      setLive(undefined)
      setIsGenerating(false)
    }
  }, [buildRequest, qc])

  const clearResult = useCallback(() => {
    setResult(undefined)
    setLive(undefined)
    setError(undefined)
  }, [])

  return {
    context,
    snapshotId,
    isLoading: data === undefined,
    summaries,
    journalExcludedCount,
    buildRequest,
    generate,
    isGenerating,
    live,
    result,
    error,
    clearResult,
  }
}

/** A cached letter costs nothing; otherwise the server prices the exact request Generate would send. */
export type CoachingCostEstimate = { cached: true } | ({ cached: false } & CoachingEstimate)

export function useCoachingEstimate(
  coaching: Pick<UseCoachingReturn, 'buildRequest' | 'snapshotId'>,
  mode: CoachingMode,
  includeFullJournal: boolean,
  model: string | undefined,
): { estimate: CoachingCostEstimate | undefined; updating: boolean } {
  const { buildRequest, snapshotId } = coaching
  const { data, isPlaceholderData } = useQuery({
    queryKey: queryKeys.coachingEstimate(snapshotId, mode, includeFullJournal, model),
    queryFn: async (): Promise<CoachingCostEstimate> => {
      const req = await buildRequest(mode, includeFullJournal, model)
      if (await getCachedSummary(req.snapshotId)) return { cached: true }
      return { cached: false, ...(await estimateCoaching(req)) }
    },
    enabled: snapshotId !== undefined,
    staleTime: Infinity,
    retry: false,
    // Keep showing the last estimate while a new style or model is priced, instead of blanking.
    placeholderData: keepPreviousData,
  })
  return { estimate: data, updating: isPlaceholderData }
}
